/* Fleet showroom media manifest.
 *
 * Maps each live vehicle id to its curated showroom media, colour and
 * environment. The cards read EVERYTHING ELSE (name, price, specs, category,
 * transmission, fuel, luggage) from the live API — this file only answers
 * "which pictures belong to this car", which the API has no field for.
 *
 * Keyed by the real API id so a rename in the CMS cannot silently orphan the
 * media. A vehicle with no entry simply falls back to its API `image`, so the
 * fleet never breaks when a car is added upstream before its media exists.
 *
 * `isRepresentative: true` means the imagery shows the correct make, model and
 * generation but was produced in a studio rather than photographed as that
 * exact physical car — the UI then shows the translated
 * "representative image" note (section 4). Set it to false only for genuine
 * photographs of the actual rental vehicle.
 *
 * Every asset's provenance is recorded in media-sources.json at the repo root.
 */

const BASE = "/fleet-media";

/** Build the showcase clip descriptor for a vehicle (orbit video + poster). */
const showcase = (slug) => ({
  mp4: `${BASE}/${slug}/showcase/${slug}-orbit.mp4`,
  webm: `${BASE}/${slug}/showcase/${slug}-orbit.webm`,
  poster: `${BASE}/${slug}/showcase/${slug}-orbit-poster.jpg`,
  posterAvif: `${BASE}/${slug}/showcase/${slug}-orbit-poster.avif`,
  /* The clip is a ~20 degree camera arc, not a full rotation, so it is
     labelled "Showcase" and never "360 Showcase" (brief section 10). */
  label: "rf_media_showcase",
});

/** Build the responsive source set for one angle. */
const angle = (slug, kind, name, widths = [400, 800, 1200]) => ({
  avif: widths.map((w) => `${BASE}/${slug}/${kind}/${slug}-${name}-${w}.avif ${w}w`).join(", "),
  webp: widths.map((w) => `${BASE}/${slug}/${kind}/${slug}-${name}-${w}.webp ${w}w`).join(", "),
  fallback: `${BASE}/${slug}/${kind}/${slug}-${name}-1200.jpg`,
});

/* Higgsfield-sourced vehicles carry a 1600px variant too, for fullscreen
   viewing on the detail page. The card never requests it: its `sizes`
   attribute caps the selection well below 1600. */
const HF_WIDTHS = [400, 800, 1200, 1600];

export const FLEET_MEDIA = {
  // Hyundai i20
  75: {
    slug: "hyundai-i20",
    colour: "navy",
    colourLabel: "fleet_colour_navy",
    environment: "casablanca",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("hyundai-i20", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("hyundai-i20", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("hyundai-i20", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("hyundai-i20", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("hyundai-i20", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Renault Clio 5
  76: {
    slug: "renault-clio-5",
    colour: "deep-red",
    colourLabel: "fleet_colour_deep_red",
    environment: "casablanca",
    isRepresentative: true,
    /* Higgsfield product-photoshoot masters (2048x1360), QC-passed.
       Replaced the earlier 1024x768 fal renders; those are backed up under
       qa/backup-fal-stills/ and the masters live in media-masters/. */
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("renault-clio-5", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("renault-clio-5", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("renault-clio-5", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("renault-clio-5", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("renault-clio-5", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: showcase("renault-clio-5"),
  },
  // Peugeot 208
  77: {
    slug: "peugeot-208",
    colour: "rich-blue",
    colourLabel: "fleet_colour_rich_blue",
    environment: "casablanca",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("peugeot-208", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("peugeot-208", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("peugeot-208", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("peugeot-208", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("peugeot-208", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Peugeot 2008
  78: {
    slug: "peugeot-2008",
    colour: "dark-grey",
    colourLabel: "fleet_colour_dark_grey",
    environment: "casablanca",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("peugeot-2008", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("peugeot-2008", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("peugeot-2008", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("peugeot-2008", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("peugeot-2008", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Dacia Duster
  79: {
    slug: "dacia-duster",
    colour: "khaki",
    colourLabel: "fleet_colour_khaki",
    environment: "atlas",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("dacia-duster", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("dacia-duster", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("dacia-duster", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("dacia-duster", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("dacia-duster", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: showcase("dacia-duster"),
  },
  // Hyundai Tucson
  80: {
    slug: "hyundai-tucson",
    colour: "black",
    colourLabel: "fleet_colour_black",
    environment: "atlas",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("hyundai-tucson", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("hyundai-tucson", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("hyundai-tucson", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("hyundai-tucson", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("hyundai-tucson", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Volkswagen T-Roc
  81: {
    slug: "volkswagen-t-roc",
    colour: "gunmetal",
    colourLabel: "fleet_colour_gunmetal",
    environment: "casablanca",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("volkswagen-t-roc", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("volkswagen-t-roc", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("volkswagen-t-roc", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("volkswagen-t-roc", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("volkswagen-t-roc", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Hyundai Tucson Premium
  82: {
    slug: "hyundai-tucson-premium",
    colour: "graphite",
    colourLabel: "fleet_colour_graphite",
    environment: "atlas",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("hyundai-tucson-premium", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("hyundai-tucson-premium", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("hyundai-tucson-premium", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("hyundai-tucson-premium", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("hyundai-tucson-premium", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: null,
  },
  // Volkswagen Tiguan
  83: {
    slug: "volkswagen-tiguan",
    colour: "navy",
    colourLabel: "fleet_colour_navy",
    environment: "atlas",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("volkswagen-tiguan", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("volkswagen-tiguan", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("volkswagen-tiguan", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("volkswagen-tiguan", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("volkswagen-tiguan", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: showcase("volkswagen-tiguan"),
  },
  // Range Rover Evoque
  84: {
    slug: "range-rover-evoque",
    colour: "silver",
    colourLabel: "fleet_colour_silver",
    environment: "marrakech",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("range-rover-evoque", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("range-rover-evoque", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("range-rover-evoque", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("range-rover-evoque", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("range-rover-evoque", "interior", "seats-front", HF_WIDTHS) },
    ],
    showcase: showcase("range-rover-evoque"),
  },
  85: {
    slug: "peugeot-3008",
    // Colour read from the vehicle's OWN approved website photograph
    // (rf-peugeot-3008-t3.png): solid white with a black roof. The stored
    // colour data for this vehicle is not modified by this rollout.
    colour: "white",
    colourLabel: "fleet_colour_white",
    environment: "casablanca",
    isRepresentative: true,
    source: "higgsfield",
    exterior: [
      { name: "front34", ...angle("peugeot-3008", "exterior", "front34", HF_WIDTHS) },
      { name: "side", ...angle("peugeot-3008", "exterior", "side", HF_WIDTHS) },
      { name: "rear34", ...angle("peugeot-3008", "exterior", "rear34", HF_WIDTHS) },
    ],
    interior: [
      { name: "dashboard", ...angle("peugeot-3008", "interior", "dashboard", HF_WIDTHS) },
      { name: "seats-front", ...angle("peugeot-3008", "interior", "seats-front", HF_WIDTHS) },
    ],
    // No approved Showcase clip for this vehicle, so the tab must not render.
    showcase: null,
  },
};

/** The card's primary (hero) angle, or null when a vehicle has no curated media. */
export const heroAngle = (id) => {
  const v = FLEET_MEDIA[id];
  return v && v.exterior.length ? v.exterior[0] : null;
};

/** The angle revealed on hover/focus — the second exterior view (section 7, Option A). */
export const hoverAngle = (id) => {
  const v = FLEET_MEDIA[id];
  return v && v.exterior.length > 1 ? v.exterior[1] : null;
};

export const vehicleMedia = (id) => FLEET_MEDIA[id] || null;

export default FLEET_MEDIA;
